import { useState } from 'react';
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconCheck, IconDownload } from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { apiUrl } from '@constants/url';
import ConsigneeImportPanel from '@/features/consignee-import/ConsigneeImportPanel';
import { downloadXlsx } from '@/features/consignee-import/download';
import type { ImportResult } from '@/features/consignee-import/types';
import { resolveFirstLoginCopy } from '../i18n';

interface StepConsigneeProps {
  /** Fires after a successful import; the wizard then offers Lanjut instead of only Lewati. */
  onImported: (result: ImportResult) => void;
  importedCount: number | null;
  /** Passed through to the import panel's apply lifecycle. */
  onApplyStateChange?: (state: 'start' | 'success' | 'error') => void;
}

/**
 * Step 2: template down, filled sheet up, review, import. Same panel the
 * Master page uses; the wizard only frames it.
 */
export default function StepConsignee({ onImported, importedCount, onApplyStateChange }: StepConsigneeProps) {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const t = (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadXlsx(apiUrl.master.consignees.template, 'Template_Consignee_JARVIS.xlsx');
    } catch (error) {
      console.warn('[first-login] template download failed', error);
      setDownloadError(t('consignee.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const card = (title: string, body: React.ReactNode) => (
    <div
      style={{
        border: `1px solid ${themeColors.border}`,
        borderRadius: 10,
        padding: 14,
        backgroundColor: themeColors.card,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <Text fw={600} style={{ color: themeColors.text }}>
        {title}
      </Text>
      {body}
    </div>
  );

  return (
    <Stack gap="md">
      <div>
        <Text component="h3" fw={700} style={{ color: themeColors.text, fontSize: 22, margin: 0, lineHeight: 1.2 }}>
          {t('consignee.heading')}
        </Text>
        <Text size="sm" style={{ color: themeColors.textSecondary, marginTop: 6 }}>
          {t('consignee.lead')}
        </Text>
      </div>

      {importedCount !== null && (
        <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
          {t('consignee.imported', { n: importedCount })}
        </Alert>
      )}

      {card(
        t('consignee.card.template.title'),
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Text size="sm" style={{ color: themeColors.textSecondary, flex: 1, minWidth: 240 }}>
            {t('consignee.card.template.body')}
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={handleDownload}
            loading={downloading}
          >
            {t('consignee.card.template.button')}
          </Button>
          {downloadError && (
            <Text size="xs" style={{ color: themeColors.error, width: '100%' }}>
              {downloadError}
            </Text>
          )}
        </Group>
      )}
      {card(
        t('consignee.card.upload.title'),
        <ConsigneeImportPanel mode="wizard" onImported={onImported} onApplyStateChange={onApplyStateChange} />
      )}
    </Stack>
  );
}
